// src/features/blog/components/BlogList.jsx
import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Tooltip,
  Menu,
  MenuItem,
  LinearProgress,
  Chip,
  Avatar,
} from "@mui/material";
import {
  Visibility,
  Edit,
  Delete,
  Publish,
  MoreVert,
  Schedule,
  Archive,
  ThumbUp,
  Comment,
  Image as ImageIcon,
} from "@mui/icons-material";
import { formatDate, getStatusLabel } from "../utils/blogHelpers";
import BlogStatusBadge from "./BlogStatusBadge";

const BlogList = ({
  posts = [],
  loading = false,
  onEdit,
  onDelete,
  onPublish,
  onPreview,
  onArchive,
  onSchedule,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);

  const handleMenuOpen = (event, post) => {
    setAnchorEl(event.currentTarget);
    setSelectedPost(post);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPost(null);
  };

  const handleAction = (action) => {
    if (!selectedPost) return;

    switch (action) {
      case "view":
        onPreview && onPreview(selectedPost);
        break;
      case "edit":
        onEdit && onEdit(selectedPost);
        break;
      case "publish":
        onPublish && onPublish(selectedPost.id);
        break;
      case "archive":
        onArchive && onArchive(selectedPost.id);
        break;
      case "delete":
        onDelete && onDelete(selectedPost.id);
        break;
      default:
        break;
    }
    handleMenuClose();
  };

  const getActions = (post) => {
    const actions = [];

    actions.push({
      key: "view",
      label: "View",
      icon: <Visibility fontSize="small" />,
    });

    if (post.status === "draft" || post.status === "pending_review") {
      actions.push({
        key: "edit",
        label: "Edit",
        icon: <Edit fontSize="small" />,
      });
      actions.push({
        key: "publish",
        label: "Publish",
        icon: <Publish fontSize="small" />,
      });
    }

    if (post.status === "published") {
      actions.push({
        key: "archive",
        label: "Archive",
        icon: <Archive fontSize="small" />,
      });
    }

    actions.push({
      key: "delete",
      label: "Delete",
      icon: <Delete fontSize="small" />,
      danger: true,
    });

    return actions;
  };

  if (loading) {
    return <LinearProgress sx={{ mt: 2 }} />;
  }

  if (!posts || posts.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          No blog posts found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create your first blog post to get started
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: "#f5f5f5" }}>
            <TableCell>Title</TableCell>
            <TableCell>Author</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Date</TableCell>
            <TableCell align="center">Stats</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {posts.map((post) => (
            <TableRow key={post.id} hover>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {post.featuredImage ? (
                    <Avatar
                      src={post.featuredImage}
                      variant="rounded"
                      sx={{ width: 40, height: 40 }}
                    />
                  ) : (
                    <Avatar
                      variant="rounded"
                      sx={{ width: 40, height: 40, bgcolor: "#667eea" }}
                    >
                      <ImageIcon />
                    </Avatar>
                  )}
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {post.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {post.excerpt
                        ? post.excerpt.substring(0, 60) + "..."
                        : ""}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {post.authorAvatar && (
                    <Avatar
                      src={post.authorAvatar}
                      sx={{ width: 24, height: 24 }}
                    />
                  )}
                  <Typography variant="body2">
                    {post.authorName || "Unknown"}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Chip label={post.category} size="small" variant="outlined" />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatDate(post.createdAt)}
                </Typography>
                {post.publishedAt && (
                  <Typography variant="caption" color="text.secondary">
                    Published: {formatDate(post.publishedAt)}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="center">
                <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                  <Tooltip title={`${post.viewCount || 0} views`}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Visibility fontSize="small" color="action" />
                      <Typography variant="caption">
                        {post.viewCount || 0}
                      </Typography>
                    </Box>
                  </Tooltip>
                  <Tooltip title={`${post.likeCount || 0} likes`}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <ThumbUp fontSize="small" color="action" />
                      <Typography variant="caption">
                        {post.likeCount || 0}
                      </Typography>
                    </Box>
                  </Tooltip>
                  <Tooltip title={`${post.commentCount || 0} comments`}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Comment fontSize="small" color="action" />
                      <Typography variant="caption">
                        {post.commentCount || 0}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell>
                <BlogStatusBadge status={post.status} />
                {post.scheduledAt && (
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                  >
                    Scheduled: {formatDate(post.scheduledAt)}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="center">
                <Box
                  sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}
                >
                  <Tooltip title="View">
                    <IconButton
                      size="small"
                      onClick={() => onPreview && onPreview(post)}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {(post.status === "draft" ||
                    post.status === "pending_review") && (
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => onEdit && onEdit(post)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  {post.status === "draft" && (
                    <Tooltip title="Publish">
                      <IconButton
                        size="small"
                        onClick={() => onPublish && onPublish(post.id)}
                        color="success"
                      >
                        <Publish fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  <Tooltip title="More">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, post)}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedPost &&
          getActions(selectedPost).map((action) => (
            <MenuItem
              key={action.key}
              onClick={() => handleAction(action.key)}
              sx={{
                color: action.danger ? "error.main" : "inherit",
              }}
            >
              {action.icon}
              <Typography variant="body2" sx={{ ml: 1 }}>
                {action.label}
              </Typography>
            </MenuItem>
          ))}
      </Menu>
    </TableContainer>
  );
};

export default BlogList;
